import * as Headless from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Badge, BadgeButton, BadgeProps } from "src/components/base/badge";
import { Link, LinkProps } from "src/components/base/link";
import { XMarkIcon } from "@heroicons/react/16/solid";
import { HighlightTerm } from "src/components/highlight-term";

/**
 * The properties for {@link EntityBadge}
 */
export type EntityBadgeProps = {
    /**
     * The entity this badge is for
     *
     * The type is intentionally not from the generated sdk
     * to support as many callers as possible
     */
    entity: { name: string; description?: string };

    /** The label prefix identifying the entity's kind, e.g. `"group"` */
    prefix: string;

    /** The route of the entity's page */
    href: LinkProps["href"];

    /** The path params for `href` */
    params: LinkProps["params"];

    /**
     * The badge's color
     *
     * This optional argument overwrites the default `"blue"`.
     */
    color?: BadgeProps["color"];

    /** Additional classname to set */
    className?: string;

    /**
     * An entity badge is a link to the entity's page.
     * This optional argument overwrites this link's default target.
     */
    target?: "_blank" | "_parent" | "_self" | "_top";

    /**
     * Optional callback to invoke when the "x" is clicked.
     *
     * If this callback is not set, then no "x" will be rendered.
     */
    onRemove?: () => void;
};

/**
 * A badge labeled `prefix:name` linking to the entity's page,
 * with an optional "x" button to remove the entity from something.
 *
 * The concrete `<GroupBadge />`, `<SubnetBadge />`, `<TagBadge />` and
 * `<DeviceGroupBadge />` are thin wrappers around this component.
 */
export function EntityBadge(props: EntityBadgeProps) {
    const { className, color, entity, prefix, href, params, target, onRemove } = props;
    const [tg] = useTranslation();

    const label = (
        <>
            {`${prefix}:`}
            <HighlightTerm value={entity.name} />
        </>
    );

    if (onRemove === undefined)
        return (
            <BadgeButton
                className={className}
                color={color ?? "blue"}
                title={entity.description}
                href={href}
                params={params}
                target={target}
            >
                {label}
            </BadgeButton>
        );
    else
        return (
            <Headless.DataInteractive>
                <span className={"group relative inline-flex rounded-md"}>
                    <Badge className={className} color={color ?? "blue"} title={entity.description}>
                        <Link href={href} params={params} target={target}>
                            {label}
                        </Link>
                        <button
                            type={"button"}
                            className={
                                "-m-0.5 cursor-pointer rounded-sm p-0.5 hover:bg-red-500/20 hover:text-red-700 dark:hover:text-red-400"
                            }
                            title={tg("button.remove")}
                            onClick={() => onRemove()}
                        >
                            <XMarkIcon className={"size-4"} />
                        </button>
                    </Badge>
                </span>
            </Headless.DataInteractive>
        );
}
